import type { VariantDef } from '../types';
import { classic, classicCategories, CLASSIC_RULES } from './classic';
import { UPPER_IDS, thresholdBonus } from './shared';

export const triple: VariantDef = {
  ...classic,
  id: 'triple',
  name: 'Triple',
  tagline: 'Three columns, triple stakes',
  description:
    'Three scorecards side by side. Column two counts double, column three triple. Thirty-nine turns of deciding where a roll is worth the most.',
  columns: [1, 2, 3],
  categories: classicCategories(),
  rules: [
    'Each turn, score your roll in any open box in any of the three columns.',
    'Column totals are multiplied: x1, x2, x3. The upper bonus and Yahtzee bonuses multiply too.',
    'Each column has its own upper bonus (35 at 63+) and its own Yahtzee bonus track.',
    ...CLASSIC_RULES.slice(0, 1),
    CLASSIC_RULES[3]!,
  ],
};

export const challenge: VariantDef = {
  ...classic,
  id: 'challenge',
  name: 'Challenge',
  tagline: 'Same dice for everyone',
  description:
    'Every player gets the identical sequence of rolls each round. No luck to blame; only the choices differ.',
  categories: classicCategories(),
  sharedRolls: true,
  rules: [
    'Each round has a fixed roll sheet: three rolls of five dice that every player receives.',
    'Holding a die keeps its value; an unheld die takes the value from the next roll in the sheet.',
    'Scoring is Classic Yahtzee: a Yahtzee is five of a kind and scores 50. The scoreboard shows who read the same dice best.',
  ],
};

/** House rule: fill the card top to bottom. */
export const straightdown: VariantDef = {
  ...classic,
  id: 'straightdown',
  name: 'Straight Down',
  tagline: 'Top to bottom, no skipping',
  family: 'house',
  description:
    'Boxes must be filled in order, Ones through Chance. Every turn has one target, so it is all about squeezing points from a forced hand.',
  categories: classicCategories(),
  sequential: true,
  upperBonus: thresholdBonus(63, 45),
  upperBonusHint: '45 points at 63 or more',
  upperCategoryIds: UPPER_IDS,
  rules: [
    'The only legal box each turn is the next open one, top to bottom.',
    'The upper bonus is raised to 45 points at 63 or more to soften the forced upper section.',
    'Yahtzee (five of a kind, 50 points) is box twelve, so plan those rolls for the home stretch.',
  ],
};

/** House rule: consecutive non-zero scores build a multiplier. */
export const streak: VariantDef = {
  ...classic,
  id: 'streak',
  name: 'Streak',
  tagline: 'Never scratch',
  family: 'house',
  description:
    'Each consecutive turn you score points, the next score is worth 10% more, up to x1.5. Scratch a box and the multiplier resets.',
  categories: classicCategories(),
  streak: { step: 0.1, maxStacks: 5 },
  rules: [
    'Score above zero on consecutive turns to build a streak.',
    'Each streak level adds 10% to the score written (x1.1, x1.2 ... up to x1.5).',
    'Writing a zero resets the streak. Bonuses are not multiplied.',
    'Otherwise Classic scoring: a Yahtzee is five of a kind and scores 50.',
  ],
};

/** House rule: three one-shot powers per game. */
export const powerplay: VariantDef = {
  ...classic,
  id: 'powerplay',
  name: 'Power Play',
  tagline: 'Three tricks up your sleeve',
  family: 'house',
  description:
    'Classic scoring plus three single-use powers per player: an extra roll, flip a die to its opposite face, or nudge a die up or down by one.',
  categories: classicCategories(),
  powers: { reroll: 1, flip: 1, nudge: 1 },
  rules: [
    'Extra roll: take a fourth roll this turn. Usable once, only after your third roll.',
    'Flip: turn one die to its opposite face (1 becomes 6, 2 becomes 5, 3 becomes 4).',
    'Nudge: move one die up or down by exactly one pip.',
    'Each power works once per game. Otherwise Classic rules: a Yahtzee is five of a kind and scores 50.',
  ],
};
